import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';

// ── Date helpers ───────────────────────────────────────────────────────
function pad(n) { return String(n).padStart(2, '0'); }
function toISO(y, m, d) { return `${y}-${pad(m)}-${pad(d)}`; }
function todayISO() { const d = new Date(); return toISO(d.getFullYear(), d.getMonth() + 1, d.getDate()); }

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// One combined "Select Month" dropdown (value "YYYY-MM") rather than
// separate Month + Year selects — keeps the whole filter row to one line.
const MONTH_YEAR_OPTIONS = (() => {
    const year = new Date().getFullYear();
    const opts = [];
    for (const y of [year - 1, year, year + 1]) {
        for (let m = 1; m <= 12; m++) opts.push({ value: `${y}-${pad(m)}`, label: `${MONTH_NAMES[m - 1]} ${y}` });
    }
    return opts;
})();

const REQUESTED_BY_OPTIONS = ["Natasha Ma'am", "Amit Sir", 'Other'];

// Known categories seen in the source sheet / the earlier (unexecuted)
// gifting schema's category list — offered as a dropdown (not a hard DB
// enum), with "Other" revealing a free-text field for anything not
// covered, since the real full list isn't confirmed anywhere in this
// codebase (the Excel's own "Lists" tab isn't readable from here).
const CATEGORY_OPTIONS = [
    'FOUNDER-GIFTING', 'INFLUENCER-BARTER', 'INFLUENCER-PAID', 'CONTEST', 'EVENT', 'SHOOT', 'HR', 'PR', 'Other',
];

const CHART_COLORS = ['#6E58A6', '#5C7E60', '#9A7B3E', '#3A6B8A', '#C25E77', '#A48FD1', '#8FBE97', '#D8B570'];

const emptyLineItem = () => ({ key: Math.random().toString(36).slice(2), product_name: '', ean_code: '', quantity: 1 });

const emptyDraft = () => ({
    request_date: todayISO(),
    requested_by: REQUESTED_BY_OPTIONS[0],
    requested_by_other: '',
    category: '',
    category_other: '',
    campaign_occasion: '',
    challan_number: '',
    customer_name: '',
    mobile_number: '',
    email: '',
    address: '',
    city: '',
    state: '',
    pin_code: '',
    instagram_handle: '',
    followers: '',
    approved_by: '',
    approval_date: '',
    approval_proof: '',
    remarks: '',
    line_items: [emptyLineItem()],
});

export default function GiftingDispatch({ user }) {
    // Default is always today, per explicit instruction — not "this month".
    const [fromDate, setFromDate] = useState(todayISO());
    const [toDate, setToDate] = useState(todayISO());
    const [quickRange, setQuickRange] = useState('today');
    const [selectedMonthYear, setSelectedMonthYear] = useState(''); // "YYYY-MM", set via the Select Month dropdown

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const [toast, setToast] = useState('');
    const [influencerOnly, setInfluencerOnly] = useState(false);
    const [requestedByFilter, setRequestedByFilter] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState('form'); // 'form' | 'review'
    const [draft, setDraft] = useState(emptyDraft());
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState('');

    // SKU typeahead — one shared piece of state tracking which line item's
    // dropdown is currently open, same "fetch full list once per keystroke,
    // filter from a single lookup endpoint" shape as InwardForm.jsx's SKU
    // picker, but reusable across however many product rows the entry has.
    const [skuOpenKey, setSkuOpenKey] = useState(null);
    const [skuResults, setSkuResults] = useState([]);
    const skuDebounceRef = useRef(null);

    const loadRows = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const qs = new URLSearchParams({ action: 'list' });
            if (fromDate) qs.set('date_from', fromDate);
            if (toDate) qs.set('date_to', toDate);
            const res = await fetch(`${API_BASE}/gifting-dispatch?${qs.toString()}`);
            const data = await res.json();
            if (!res.ok || data.success === false) throw new Error(data.error || 'Failed to load');
            setRows(data.rows || []);
        } catch (err) {
            setError(err.message);
        }
        setLoading(false);
    }, [fromDate, toDate]);

    useEffect(() => { loadRows(); }, [loadRows]);

    function applyQuickRange(key) {
        setQuickRange(key);
        setSelectedMonthYear('');
        if (key === 'today') { setFromDate(todayISO()); setToDate(todayISO()); }
    }
    function onManualDateChange(setter, value) {
        setter(value);
        setQuickRange(null);
        setSelectedMonthYear('');
    }
    // "Select Month" dropdown ("YYYY-MM") — expands to that month's real
    // first/last day (day 0 of the next month = the last day of this one),
    // same technique GRN Management's own applyMonthYear uses.
    function applyMonthYear(value) {
        setSelectedMonthYear(value);
        setQuickRange(null);
        if (!value) return;
        const [y, m] = value.split('-').map(Number);
        setFromDate(toISO(y, m, 1));
        setToDate(toISO(y, m, new Date(y, m, 0).getDate()));
    }

    const filteredRows = useMemo(() => {
        let list = rows;
        if (influencerOnly) list = list.filter(r => (r.category || '').toUpperCase().startsWith('INFLUENCER'));
        if (requestedByFilter) list = list.filter(r => r.requested_by === requestedByFilter);
        if (categoryFilter) list = list.filter(r => (r.category || '').toUpperCase() === categoryFilter.toUpperCase());
        const q = search.trim().toLowerCase();
        if (!q) return list;
        return list.filter(r =>
            `${r.entry_id}${r.customer_name || ''}${r.product_name || ''}${r.ean_code || ''}${r.category || ''}${r.challan_number || ''}${r.instagram_handle || ''}`
                .toLowerCase().includes(q)
        );
    }, [rows, search, influencerOnly, requestedByFilter, categoryFilter]);

    // Every distinct category actually present in the fetched rows, so the
    // Category filter dropdown always offers real options for this data —
    // not just the fixed CATEGORY_OPTIONS list used by the Add Entry form
    // (custom-typed "Other" categories should be filterable too).
    const distinctCategories = useMemo(() => {
        const set = new Set(rows.map(r => r.category).filter(Boolean));
        return Array.from(set).sort();
    }, [rows]);

    // Label shown on the Summary Report card so it's clear which range the
    // numbers below reflect — always driven by the one shared top-level
    // date range, never a separate filter of its own.
    const rangeLabel = useMemo(() => {
        if (quickRange === 'today') return 'Today';
        if (selectedMonthYear) return MONTH_YEAR_OPTIONS.find(o => o.value === selectedMonthYear)?.label || selectedMonthYear;
        if (fromDate && toDate) return fromDate === toDate ? formatDate(fromDate) : `${formatDate(fromDate)} – ${formatDate(toDate)}`;
        return 'All time';
    }, [quickRange, selectedMonthYear, fromDate, toDate]);

    // ── Dashboard summary — computed client-side from the already-fetched
    // date-range rows, same pattern as SoVsInvoice.jsx's summary. Reflects
    // the date-range scope only (not search/influencer-toggle), so the
    // numbers stay stable while someone's just filtering the table below.
    const summary = useMemo(() => {
        const entryIds = new Set();
        const customers = new Set();
        let totalQuantity = 0;
        const byCategory = {};
        const byRequester = {};
        const byProduct = {};

        for (const r of rows) {
            entryIds.add(r.entry_id);
            if (r.customer_name) customers.add(r.customer_name);
            const qty = Number(r.quantity) || 0;
            totalQuantity += qty;

            const cat = r.category || 'Uncategorized';
            byCategory[cat] = (byCategory[cat] || 0) + qty;

            const who = r.requested_by === 'Other' ? (r.requested_by_other || 'Other') : (r.requested_by || 'Unspecified');
            byRequester[who] = (byRequester[who] || 0) + qty;

            // Stock Deduction Summary — per product/EAN, for the inventory
            // manager to actually subtract from stock. Rows with no product
            // at all (a header-only entry) are skipped here; they carry
            // nothing to deduct.
            if (r.product_name || r.ean_code) {
                const key = r.ean_code || r.product_name;
                if (!byProduct[key]) byProduct[key] = { product_name: r.product_name || '(unnamed product)', ean_code: r.ean_code || '—', quantity: 0 };
                byProduct[key].quantity += qty;
            }
        }

        const toChartData = (obj) => Object.entries(obj)
            .sort((a, b) => b[1] - a[1])
            .map(([label, value], i) => ({ label, value, color: CHART_COLORS[i % CHART_COLORS.length] }));

        return {
            totalEntries: entryIds.size,
            totalCustomers: customers.size,
            totalLineItems: rows.length,
            totalQuantity,
            byCategory: toChartData(byCategory),
            byRequester: toChartData(byRequester),
            byProduct: Object.values(byProduct).sort((a, b) => b.quantity - a.quantity),
        };
    }, [rows]);

    function showToast(msg) {
        setToast(msg);
        setTimeout(() => setToast(''), 3000);
    }

    // ── SKU typeahead ────────────────────────────────────────────────────
    // `field` tells us which input triggered the search — when it's the EAN
    // field and the typed value is an EXACT match for one sku_master row,
    // the product name is filled in immediately (no click needed), per the
    // explicit "even if we type EAN code then product name should come
    // automatically" requirement. Typing a product name still requires
    // picking from the dropdown, since many SKUs can share a similar name.
    function searchSku(key, q, field) {
        setSkuOpenKey(key);
        clearTimeout(skuDebounceRef.current);
        if (!q || q.trim().length < 2) { setSkuResults([]); return; }
        const trimmed = q.trim();
        skuDebounceRef.current = setTimeout(async () => {
            try {
                const res = await fetch(`${API_BASE}/gifting-dispatch?action=sku_lookup&q=${encodeURIComponent(trimmed)}`);
                const data = await res.json();
                const skus = data.skus || [];
                setSkuResults(skus);
                if (field === 'ean_code') {
                    const exact = skus.find(s => String(s.ean_number).trim().toLowerCase() === trimmed.toLowerCase());
                    if (exact) pickSku(key, exact);
                }
            } catch {
                setSkuResults([]);
            }
        }, 250);
    }
    function pickSku(key, sku) {
        setDraft(d => ({
            ...d,
            line_items: d.line_items.map(li => li.key === key ? { ...li, product_name: sku.product_name, ean_code: sku.ean_number || '' } : li),
        }));
        setSkuOpenKey(null);
        setSkuResults([]);
    }

    function updateLineItem(key, field, value) {
        setDraft(d => ({ ...d, line_items: d.line_items.map(li => li.key === key ? { ...li, [field]: value } : li) }));
        if (field === 'product_name' || field === 'ean_code') searchSku(key, value, field);
    }
    function addLineItem() {
        setDraft(d => ({ ...d, line_items: [...d.line_items, emptyLineItem()] }));
    }
    function removeLineItem(key) {
        setDraft(d => ({ ...d, line_items: d.line_items.length > 1 ? d.line_items.filter(li => li.key !== key) : d.line_items }));
    }

    function openModal() {
        setDraft(emptyDraft());
        setSaveError('');
        setModalStep('form');
        setModalOpen(true);
    }
    function closeModal() {
        setModalOpen(false);
        setModalStep('form');
        setSkuOpenKey(null);
        setSkuResults([]);
    }

    // Only request_date/requested_by (and requested_by_other/category_other
    // when "Other" is picked) are actually required — everything else,
    // including every product line, is optional per explicit instruction.
    function goToReview() {
        setSaveError('');
        if (!draft.request_date) { setSaveError('Request Date is required'); return; }
        if (!draft.requested_by) { setSaveError('Requested By is required'); return; }
        if (draft.requested_by === 'Other' && !draft.requested_by_other.trim()) { setSaveError('Enter the requester’s name'); return; }
        if (draft.category === 'Other' && !draft.category_other.trim()) { setSaveError('Enter the category / event name'); return; }
        setModalStep('review');
    }

    async function confirmSave() {
        setSaving(true);
        setSaveError('');
        try {
            const payload = {
                ...draft,
                category: draft.category === 'Other' ? draft.category_other.trim().toUpperCase() : draft.category,
                followers: draft.followers ? Number(draft.followers) : null,
                approval_date: draft.approval_date || null,
                created_by: user?.display_name || user?.username || '',
                line_items: draft.line_items.map(li => ({
                    product_name: li.product_name.trim() || null,
                    ean_code: li.ean_code.trim() || null,
                    quantity: li.quantity ? Number(li.quantity) : null,
                })),
            };
            const res = await fetch(`${API_BASE}/gifting-dispatch?action=save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            const data = await res.json();
            if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
            showToast(`✓ Saved ${data.entry_id} (${data.line_items} product${data.line_items > 1 ? 's' : ''})`);
            closeModal();
            loadRows();
        } catch (err) {
            setSaveError(err.message);
        }
        setSaving(false);
    }

    function downloadExcel() {
        if (!window.XLSX || filteredRows.length === 0) return;
        const data = filteredRows.map(r => ({
            'Entry ID': r.entry_id,
            'Request Date': formatDate(r.request_date),
            'Requested By': r.requested_by === 'Other' ? r.requested_by_other : r.requested_by,
            'Category': r.category ? r.category.toUpperCase() : '',
            'Campaign / Occasion': r.campaign_occasion || '',
            'Challan Number': r.challan_number || '',
            'Customer Name': r.customer_name,
            'Mobile Number': r.mobile_number || '',
            'Email': r.email || '',
            'Address': r.address || '',
            'City': r.city || '',
            'State': r.state || '',
            'PIN Code': r.pin_code || '',
            'Instagram Handle': r.instagram_handle || '',
            'Followers': r.followers ?? '',
            'Product Name': r.product_name,
            'EAN Code': r.ean_code || '',
            'Quantity': r.quantity,
            'Approved By': r.approved_by || '',
            'Approval Date': formatDate(r.approval_date),
            'Approval Proof': r.approval_proof || '',
            'Remarks': r.remarks || '',
        }));
        const ws = window.XLSX.utils.json_to_sheet(data);
        ws['!cols'] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(12, Math.min(30, k.length + 6)) }));
        const wb = window.XLSX.utils.book_new();
        window.XLSX.utils.book_append_sheet(wb, ws, 'Gifting & Dispatch');
        window.XLSX.writeFile(wb, `Gifting_Dispatch_${fromDate || 'all'}_to_${toDate || 'all'}.xlsx`);
    }

    return (
        <div style={S.page}>
            <style>{EMBEDDED_CSS}</style>

            {toast && <div style={S.toast}>{toast}</div>}

            {/* ── Decorative hero header — also holds every filter/control ─── */}
            <div style={S.hero}>
                <div className="gd-hero-bg">
                    <span className="gd-float gd-float-1">🎁</span>
                    <span className="gd-float gd-float-2">✨</span>
                    <span className="gd-float gd-float-3">🎀</span>
                    <span className="gd-float gd-float-4">🌸</span>
                    <span className="gd-float gd-float-5">💐</span>
                    <span className="gd-float gd-float-6">🎁</span>
                </div>
                <div style={S.heroContent}>
                    <div>
                        <div style={S.kicker}>PO &amp; SO Lifecycle</div>
                        <h1 style={S.title}>Gifting &amp; <b>Dispatch</b></h1>
                        <p style={S.subtitle}>Track every gift hamper, influencer package and founder dispatch — from request to approved delivery.</p>
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button className="gd-btn" style={{ ...S.influencerBtn, ...(influencerOnly ? S.influencerBtnActive : {}) }} onClick={() => setInfluencerOnly(v => !v)}>
                            🤳 Influencer Records
                        </button>
                        <button className="gd-btn" style={S.downloadBtn} onClick={downloadExcel} disabled={filteredRows.length === 0}>⬇ Download Excel</button>
                        <button className="gd-btn" style={S.addBtn} onClick={openModal}>+ Add New Entry</button>
                    </div>
                </div>

                {/* Exactly the filters asked for, in one line: Today, date
                    range, Select Month, All Requesters, All Categories,
                    Search. No other quick-range buttons. */}
                <div style={S.heroControlsRow}>
                    <button className="gd-btn" style={{ ...S.heroQuickBtn, ...(quickRange === 'today' ? S.heroQuickBtnActive : {}), flexShrink: 0 }} onClick={() => applyQuickRange('today')}>
                        Today
                    </button>
                    <input className="gd-hero-input" style={{ ...S.heroInput, colorScheme: 'dark', flexShrink: 0 }} type="date" value={fromDate} onChange={e => onManualDateChange(setFromDate, e.target.value)} />
                    <span style={{ color: '#9A90AE', fontSize: 12.5, flexShrink: 0 }}>to</span>
                    <input className="gd-hero-input" style={{ ...S.heroInput, colorScheme: 'dark', flexShrink: 0 }} type="date" value={toDate} onChange={e => onManualDateChange(setToDate, e.target.value)} />
                    <select className="gd-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={selectedMonthYear} onChange={e => applyMonthYear(e.target.value)}>
                        <option value="">Select Month</option>
                        {MONTH_YEAR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select className="gd-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={requestedByFilter} onChange={e => setRequestedByFilter(e.target.value)}>
                        <option value="">All Requesters</option>
                        {REQUESTED_BY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <select className="gd-hero-input" style={{ ...S.heroSelect, flexShrink: 0 }} value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}>
                        <option value="">All Categories</option>
                        {distinctCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input className="gd-hero-input" style={{ ...S.heroInput, flex: 1, minWidth: 100 }} placeholder="🔍 Search…" value={search} onChange={e => setSearch(e.target.value)} />
                </div>
                <p style={S.heroShowingText}>Showing <b style={{ color: '#fff' }}>{filteredRows.length}</b> of {rows.length} line items{influencerOnly ? ' — Influencer Records only' : ''}</p>
            </div>

            {/* ── Summary Report — compact charts + per-product stock deduction ── */}
            <div style={S.summaryCard}>
                <div style={S.summaryHeader}>
                    <span style={S.chartTitle}>📦 Summary Report</span>
                    <span style={S.summaryRangeBadge}>{rangeLabel}</span>
                </div>
                <div style={S.summaryGrid}>
                    <div style={S.kpiRow}>
                        <KpiTile label="Total Entries" value={summary.totalEntries} />
                        <KpiTile label="Unique Customers" value={summary.totalCustomers} />
                        <KpiTile label="Product Lines" value={summary.totalLineItems} />
                        <KpiTile label="Total Quantity Dispatched" value={summary.totalQuantity} accent />
                    </div>
                    <div style={S.summaryBottomRow}>
                        <div style={S.compactChartsCol}>
                            <ChartCard title="By Category" data={summary.byCategory} compact />
                            <ChartCard title="By Requested By" data={summary.byRequester} compact />
                        </div>
                        <div style={S.stockCard}>
                            <div style={S.chartTitle}>Stock Deduction Summary — {rangeLabel}</div>
                            <p style={{ fontSize: 11.5, color: 'var(--gd-text-faint)', margin: '-8px 0 12px' }}>Total quantity dispatched per product in this range — for inventory to deduct from stock.</p>
                            {summary.byProduct.length === 0 ? (
                                <p style={{ ...S.emptyState, padding: '20px 8px' }}>No products dispatched in this range.</p>
                            ) : (
                                <div style={S.stockTableWrap}>
                                    <table style={S.reviewTable}>
                                        <thead>
                                            <tr>
                                                <th style={S.reviewHeadCell}>Product</th>
                                                <th style={S.reviewHeadCell}>EAN</th>
                                                <th style={S.reviewHeadCell}>Qty to Deduct</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {summary.byProduct.map(p => (
                                                <tr key={p.ean_code + p.product_name}>
                                                    <td style={S.reviewValue}>{p.product_name}</td>
                                                    <td style={S.reviewValue}>{p.ean_code}</td>
                                                    <td style={{ ...S.reviewValue, fontWeight: 700, color: 'var(--gd-purple)' }}>{p.quantity.toLocaleString()}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Table ───────────────────────────────────────────────────── */}
            {loading ? (
                <p style={S.emptyState}>Loading…</p>
            ) : error ? (
                <p style={{ ...S.emptyState, color: 'var(--gd-error)' }}>Failed to load: {error}</p>
            ) : filteredRows.length === 0 ? (
                <p style={S.emptyState}>No gifting entries in this range.</p>
            ) : (
                <div style={S.card}>
                    <div style={S.scrollWrap}>
                        <div style={{ minWidth: 980 }}>
                            <div style={{ ...S.gridRow, ...S.gridHeader, gridTemplateColumns: COLS }}>
                                {HEADERS.map(h => <div key={h} style={S.headCell}>{h}</div>)}
                            </div>
                            {filteredRows.map((r, idx) => (
                                <div key={r.id} className="gd-row" style={{ ...S.gridRow, gridTemplateColumns: COLS }}>
                                    <div style={{ ...S.cell, color: 'var(--gd-text-faint)' }}>{idx + 1}</div>
                                    <div style={{ ...S.cell, fontWeight: 700, color: 'var(--gd-purple)' }}>{r.entry_id}</div>
                                    <div style={S.cell}>{formatDate(r.request_date)}</div>
                                    <div style={S.cell}>{r.requested_by === 'Other' ? r.requested_by_other : r.requested_by}</div>
                                    <div style={S.cell}>{r.category ? r.category.toUpperCase() : '—'}</div>
                                    <div style={{ ...S.cell, fontWeight: 600 }}>{r.customer_name || '—'}</div>
                                    <div style={S.cell}>{r.product_name || '—'}</div>
                                    <div style={S.cell}>{r.ean_code || '—'}</div>
                                    <div style={{ ...S.cell, ...S.numTab, fontWeight: 700 }}>{r.quantity ?? '—'}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Add Entry modal ─────────────────────────────────────────── */}
            {modalOpen && (
                <div style={S.modalOverlay} onClick={closeModal}>
                    <div style={S.modal} onClick={e => e.stopPropagation()}>
                        <div style={S.modalHeader}>
                            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{modalStep === 'form' ? 'New Gifting Entry' : 'Review Entry'}</h3>
                            <button style={S.closeBtn} onClick={closeModal}>✕</button>
                        </div>

                        <div style={S.modalBody}>
                            {saveError && <div style={S.errorBanner}>{saveError}</div>}

                            {modalStep === 'form' ? (<>
                            <div style={S.formGrid}>
                                <Field label="Request Date" required>
                                    <input className="gd-input" type="date" value={draft.request_date} onChange={e => setDraft(d => ({ ...d, request_date: e.target.value }))} />
                                </Field>
                                <Field label="Requested By" required>
                                    <select className="gd-input" value={draft.requested_by} onChange={e => setDraft(d => ({ ...d, requested_by: e.target.value }))}>
                                        {REQUESTED_BY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                                    </select>
                                </Field>
                                {draft.requested_by === 'Other' && (
                                    <Field label="Requester Name" required>
                                        <input className="gd-input" value={draft.requested_by_other} onChange={e => setDraft(d => ({ ...d, requested_by_other: e.target.value }))} placeholder="Enter name" />
                                    </Field>
                                )}
                                <Field label="Category">
                                    <select className="gd-input" value={draft.category} onChange={e => setDraft(d => ({ ...d, category: e.target.value }))}>
                                        <option value="">— Select —</option>
                                        {CATEGORY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </Field>
                                {draft.category === 'Other' && (
                                    <Field label="Category / Event Name" required>
                                        <input className="gd-input" value={draft.category_other} onChange={e => setDraft(d => ({ ...d, category_other: e.target.value.toUpperCase() }))} placeholder="TYPE THE EVENT/CATEGORY" />
                                    </Field>
                                )}
                                <Field label="Campaign / Occasion">
                                    <input className="gd-input" value={draft.campaign_occasion} onChange={e => setDraft(d => ({ ...d, campaign_occasion: e.target.value }))} />
                                </Field>
                                <Field label="Challan Number">
                                    <input className="gd-input" value={draft.challan_number} onChange={e => setDraft(d => ({ ...d, challan_number: e.target.value }))} />
                                </Field>
                            </div>

                            <div style={S.sectionTitle}>Customer Details</div>
                            <div style={S.formGrid}>
                                <Field label="Customer Name">
                                    <input className="gd-input" value={draft.customer_name} onChange={e => setDraft(d => ({ ...d, customer_name: e.target.value }))} />
                                </Field>
                                <Field label="Mobile Number">
                                    <input className="gd-input" value={draft.mobile_number} onChange={e => setDraft(d => ({ ...d, mobile_number: e.target.value }))} />
                                </Field>
                                <Field label="Email">
                                    <input className="gd-input" type="email" value={draft.email} onChange={e => setDraft(d => ({ ...d, email: e.target.value }))} />
                                </Field>
                                <Field label="Address" span={2}>
                                    <input className="gd-input" value={draft.address} onChange={e => setDraft(d => ({ ...d, address: e.target.value }))} />
                                </Field>
                                <Field label="City">
                                    <input className="gd-input" value={draft.city} onChange={e => setDraft(d => ({ ...d, city: e.target.value }))} />
                                </Field>
                                <Field label="State">
                                    <input className="gd-input" value={draft.state} onChange={e => setDraft(d => ({ ...d, state: e.target.value }))} />
                                </Field>
                                <Field label="PIN Code">
                                    <input className="gd-input" value={draft.pin_code} onChange={e => setDraft(d => ({ ...d, pin_code: e.target.value }))} />
                                </Field>
                                <Field label="Instagram Handle">
                                    <input className="gd-input" value={draft.instagram_handle} onChange={e => setDraft(d => ({ ...d, instagram_handle: e.target.value }))} placeholder="@handle" />
                                </Field>
                                <Field label="Followers">
                                    <input className="gd-input" type="number" min="0" value={draft.followers} onChange={e => setDraft(d => ({ ...d, followers: e.target.value }))} />
                                </Field>
                            </div>

                            <div style={S.sectionTitle}>Products</div>
                            <div style={S.lineItemHeaderRow}>
                                <div style={{ flex: '1.6 1 0' }}>Product</div>
                                <div style={{ flex: '0.9 1 0' }}>EAN</div>
                                <div style={{ flex: '0.5 1 0' }}>Quantity</div>
                                <div style={{ width: 34 }} />
                            </div>
                            {draft.line_items.map((li, i) => (
                                <div key={li.key} style={S.lineItemRow}>
                                    <div style={{ flex: '1.6 1 0', minWidth: 0, position: 'relative' }}>
                                        <input className="gd-input" placeholder="Product name" value={li.product_name}
                                            onChange={e => updateLineItem(li.key, 'product_name', e.target.value)}
                                            onFocus={() => searchSku(li.key, li.product_name, 'product_name')} />
                                        {skuOpenKey === li.key && skuResults.length > 0 && (
                                            <div style={S.skuDropdown}>
                                                {skuResults.map(s => (
                                                    <div key={s.ean_number + s.product_name} className="gd-sku-option" style={S.skuOption} onClick={() => pickSku(li.key, s)}>
                                                        <span>{s.product_name}</span>
                                                        <span style={{ color: 'var(--gd-text-faint)', fontSize: 11.5 }}>{s.ean_number}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    <div style={{ flex: '0.9 1 0', minWidth: 0 }}>
                                        <input className="gd-input" placeholder="EAN code" value={li.ean_code}
                                            onChange={e => updateLineItem(li.key, 'ean_code', e.target.value)}
                                            onFocus={() => searchSku(li.key, li.ean_code, 'ean_code')} />
                                    </div>
                                    <div style={{ flex: '0.5 1 0', minWidth: 0 }}>
                                        <input className="gd-input" type="number" min="1" placeholder="Qty" value={li.quantity}
                                            onChange={e => updateLineItem(li.key, 'quantity', e.target.value)} />
                                    </div>
                                    <button style={S.removeLineBtn} onClick={() => removeLineItem(li.key)} disabled={draft.line_items.length === 1}>✕</button>
                                </div>
                            ))}
                            <button className="gd-btn" style={S.addLineBtn} onClick={addLineItem}>+ Add Product</button>

                            <div style={S.sectionTitle}>Approval</div>
                            <div style={S.formGrid}>
                                <Field label="Approved By">
                                    <input className="gd-input" value={draft.approved_by} onChange={e => setDraft(d => ({ ...d, approved_by: e.target.value }))} />
                                </Field>
                                <Field label="Approval Date">
                                    <input className="gd-input" type="date" value={draft.approval_date} onChange={e => setDraft(d => ({ ...d, approval_date: e.target.value }))} />
                                </Field>
                                <Field label="Approval Proof" span={2}>
                                    <input className="gd-input" value={draft.approval_proof} onChange={e => setDraft(d => ({ ...d, approval_proof: e.target.value }))} placeholder="e.g. Approved via WhatsApp on 20 Oct" />
                                </Field>
                                <Field label="Remarks" span={2}>
                                    <input className="gd-input" value={draft.remarks} onChange={e => setDraft(d => ({ ...d, remarks: e.target.value }))} />
                                </Field>
                            </div>
                            </>) : (
                                <ReviewSummary draft={draft} />
                            )}
                        </div>

                        <div style={S.modalFooter}>
                            {modalStep === 'form' ? (<>
                                <button className="gd-btn" style={S.cancelBtn} onClick={closeModal}>Cancel</button>
                                <button className="gd-btn" style={S.saveBtn} onClick={goToReview}>Review Entry →</button>
                            </>) : (<>
                                <button className="gd-btn" style={S.cancelBtn} onClick={() => setModalStep('form')}>← Go Back</button>
                                <button className="gd-btn" style={{ ...S.saveBtn, opacity: saving ? 0.6 : 1 }} onClick={confirmSave} disabled={saving}>
                                    {saving ? 'Saving…' : '✓ Save & Continue'}
                                </button>
                            </>)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, required, span, children }) {
    return (
        <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
            <div style={S.fieldLabel}>{label}{required && <span style={{ color: 'var(--gd-error)' }}> *</span>}</div>
            {children}
        </div>
    );
}

function KpiTile({ label, value, accent }) {
    return (
        <div style={S.kpiTile}>
            <div style={{ fontSize: 26, fontWeight: 700, color: accent ? 'var(--gd-purple)' : 'var(--gd-text)' }}>{(value ?? 0).toLocaleString()}</div>
            <div style={S.kpiLabel}>{label}</div>
        </div>
    );
}

// Lightweight hand-rolled SVG donut chart — same approach GRN Management's
// own DonutChart uses, rather than adding a charting library dependency
// for one page.
function ChartCard({ title, data, compact }) {
    const total = data.reduce((s, d) => s + d.value, 0);
    const size = compact ? 92 : 140;
    const mid = size / 2;
    const R = compact ? 34 : 54;
    const strokeW = compact ? 13 : 18;
    const C = 2 * Math.PI * R;
    const legendLimit = compact ? 4 : 6;
    let offset = 0;

    return (
        <div style={compact ? S.chartCardCompact : S.chartCard}>
            <div style={compact ? S.chartTitleCompact : S.chartTitle}>{title}</div>
            {total === 0 ? (
                <p style={{ ...S.emptyState, padding: compact ? '10px 4px' : '24px 8px', fontSize: compact ? 11.5 : 14 }}>No data in this range.</p>
            ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: compact ? 10 : 20, flexWrap: 'wrap' }}>
                    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
                        <g transform={`translate(${mid},${mid}) rotate(-90)`}>
                            <circle r={R} fill="none" stroke="var(--gd-border)" strokeWidth={strokeW} />
                            {data.map((d) => {
                                const frac = d.value / total;
                                const dash = frac * C;
                                const circle = (
                                    <circle key={d.label} r={R} fill="none" stroke={d.color} strokeWidth={strokeW}
                                        strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-offset} />
                                );
                                offset += dash;
                                return circle;
                            })}
                        </g>
                        {!compact && (<>
                            <text x={mid} y={mid - 5} textAnchor="middle" fontSize="18" fontWeight="700" fill="var(--gd-text)">{total.toLocaleString()}</text>
                            <text x={mid} y={mid + 12} textAnchor="middle" fontSize="10" fill="var(--gd-text-faint)">units</text>
                        </>)}
                    </svg>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: compact ? 3 : 6, flex: 1, minWidth: compact ? 90 : 120 }}>
                        {data.slice(0, legendLimit).map(d => (
                            <div key={d.label} style={compact ? S.legendRowCompact : S.legendRow}>
                                <span style={{ ...S.legendDot, background: d.color }} />
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</span>
                                <span style={{ fontWeight: 700 }}>{d.value.toLocaleString()}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// Read-only tabular confirmation shown before the actual save — per the
// explicit "a final template showing all details in tabular format...
// then click save and continue or go back" requirement.
function ReviewSummary({ draft }) {
    const requestedByDisplay = draft.requested_by === 'Other' ? draft.requested_by_other : draft.requested_by;
    const categoryDisplay = draft.category === 'Other' ? draft.category_other : draft.category;
    const headerRows = [
        ['Request Date', draft.request_date],
        ['Requested By', requestedByDisplay],
        ['Category', categoryDisplay],
        ['Campaign / Occasion', draft.campaign_occasion],
        ['Challan Number', draft.challan_number],
        ['Customer Name', draft.customer_name],
        ['Mobile Number', draft.mobile_number],
        ['Email', draft.email],
        ['Address', draft.address],
        ['City', draft.city],
        ['State', draft.state],
        ['PIN Code', draft.pin_code],
        ['Instagram Handle', draft.instagram_handle],
        ['Followers', draft.followers],
        ['Approved By', draft.approved_by],
        ['Approval Date', draft.approval_date],
        ['Approval Proof', draft.approval_proof],
        ['Remarks', draft.remarks],
    ].filter(([, v]) => v !== '' && v !== null && v !== undefined);

    const products = draft.line_items.filter(li => li.product_name || li.ean_code || li.quantity);

    return (
        <div>
            <div style={S.sectionTitle}>Entry Details</div>
            <table style={S.reviewTable}>
                <tbody>
                    {headerRows.map(([label, value]) => (
                        <tr key={label}>
                            <td style={S.reviewLabel}>{label}</td>
                            <td style={S.reviewValue}>{String(value)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <div style={S.sectionTitle}>Products ({products.length})</div>
            {products.length === 0 ? (
                <p style={{ color: 'var(--gd-text-faint)', fontSize: 13 }}>No products added.</p>
            ) : (
                <table style={S.reviewTable}>
                    <thead>
                        <tr>
                            <th style={S.reviewHeadCell}>Product</th>
                            <th style={S.reviewHeadCell}>EAN</th>
                            <th style={S.reviewHeadCell}>Quantity</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map(li => (
                            <tr key={li.key}>
                                <td style={S.reviewValue}>{li.product_name || '—'}</td>
                                <td style={S.reviewValue}>{li.ean_code || '—'}</td>
                                <td style={S.reviewValue}>{li.quantity || '—'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <p style={{ fontSize: 12.5, color: 'var(--gd-text-faint)', marginTop: 14 }}>
                Check everything above, then confirm to save — this will generate a new Entry ID and write to the record.
            </p>
        </div>
    );
}

// On-screen table shows only these 8 (+ serial) columns, per explicit
// instruction — every other field (Campaign/Occasion, Challan Number,
// Mobile/Email/Address/City/State/PIN, Instagram/Followers, Approved
// By/Approval Date/Approval Proof, Remarks) still goes into the Excel
// export (see downloadExcel()), just not onto the screen.
const HEADERS = ['#', 'Entry ID', 'Request Date', 'Requested By', 'Category', 'Customer Name', 'Product Name', 'EAN Code', 'Quantity'];
const COLS = '0.4fr 0.9fr 1fr 1.1fr 1.2fr 1.4fr 2fr 1fr 0.8fr';

/* ---------------------------------------------------------------------- */
/* Styles — self-scoped CSS variables mirroring GRN Management's exact    */
/* token values, for visual consistency across the PO & SO Lifecycle      */
/* section this page lives in.                                            */
/* ---------------------------------------------------------------------- */
const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&display=swap');
:root {
  --gd-surface: #fff; --gd-surface-subtle: #FBFAFD;
  --gd-border: #ECE7F2; --gd-border-soft: #E4DEEE;
  --gd-text: #231C30; --gd-text-muted: #8C82A0; --gd-text-faint: #9A90AE;
  --gd-accent-surface: #231C30;
  --gd-purple: #6E58A6; --gd-purple-tint: #EEE9F8;
  --gd-green: #5C7E60; --gd-green-tint: #E7F0E8;
  --gd-error: #C25E77; --gd-error-tint: #F7E5EA;
  --gd-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --gd-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
}
.gd-btn { font-family: 'Space Grotesk', sans-serif; transition: transform 0.15s ease, background 0.2s ease; cursor: pointer; }
.gd-btn:active:not(:disabled) { transform: scale(0.96); }
.gd-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gd-row:nth-child(even) { background: var(--gd-surface-subtle); }
.gd-row:hover { background: var(--gd-purple-tint); }
.gd-input { font-family: 'Space Grotesk', sans-serif; padding: 10px 14px; border-radius: 10px; border: 1px solid var(--gd-border-soft); background: #fff; color: var(--gd-text); font-size: 13.5px; width: 100%; box-sizing: border-box; }
.gd-input:focus { border-color: var(--gd-purple) !important; box-shadow: 0 0 0 3px rgba(110,88,166,0.18); outline: none; }
.gd-hero-input:focus { border-color: #A48FD1 !important; box-shadow: 0 0 0 3px rgba(164,143,209,0.25) !important; outline: none; }
/* App.css forces a white background on every focused input/select, which
   left the white-text hero filters (and their open dropdown) white-on-white.
   Keep them dark while focused, and give the dropdown options a dark base. */
.gd-hero-input:focus { background: #2A2140 !important; color: #fff !important; }
select.gd-hero-input { color-scheme: dark; }
.gd-hero-input option { background: #2A2140; color: #fff; }
.gd-hero-input option:checked { background: #6E58A6; color: #fff; }
.gd-hero-input::placeholder { color: #9A90AE; }
.gd-sku-option:hover { background: var(--gd-purple-tint); }
* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }
*::-webkit-scrollbar { width: 8px; height: 8px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(110,88,166,0.35); border-radius: 999px; }

.gd-hero-bg { position: absolute; inset: 0; overflow: hidden; border-radius: 20px; pointer-events: none; }
.gd-float { position: absolute; font-size: 34px; opacity: 0.16; animation: gd-drift 9s ease-in-out infinite; filter: drop-shadow(0 4px 10px rgba(0,0,0,0.25)); }
.gd-float-1 { top: 12%; left: 6%;  animation-duration: 8s; }
.gd-float-2 { top: 60%; left: 14%; animation-duration: 10s; animation-delay: 1s; }
.gd-float-3 { top: 20%; left: 88%; animation-duration: 7.5s; animation-delay: 0.5s; }
.gd-float-4 { top: 70%; left: 80%; animation-duration: 9.5s; animation-delay: 2s; }
.gd-float-5 { top: 40%; left: 48%; animation-duration: 11s; animation-delay: 1.5s; }
.gd-float-6 { top: 15%; left: 62%; animation-duration: 8.5s; animation-delay: 0.8s; }
@keyframes gd-drift {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  50% { transform: translateY(-16px) rotate(8deg); }
}
`;

const S = {
    page: { padding: 28, fontFamily: "'Space Grotesk', sans-serif", color: 'var(--gd-text)' },
    toast: { position: 'fixed', top: 18, left: '50%', transform: 'translateX(-50%)', background: '#231C30', color: '#fff', padding: '10px 20px', borderRadius: 20, fontSize: 13, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.25)' },
    hero: { position: 'relative', background: 'linear-gradient(135deg, #2A2140 0%, #1A1529 55%, #120D1D 100%)', border: '1px solid #2A2338', borderRadius: 20, padding: '32px 32px', marginBottom: 20, overflow: 'hidden', boxShadow: '0 10px 30px rgba(20,16,31,0.3)' },
    heroContent: { position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 20, flexWrap: 'wrap' },
    kicker: { fontSize: 12, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#A48FD1' },
    title: { fontSize: 30, fontWeight: 500, margin: '6px 0 10px', color: '#fff', letterSpacing: '-0.01em' },
    subtitle: { fontSize: 14.5, color: '#B2AAC4', margin: 0, maxWidth: 480, lineHeight: 1.55 },
    addBtn: { padding: '12px 20px', borderRadius: 10, border: 'none', background: 'var(--gd-purple)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
    downloadBtn: { padding: '12px 20px', borderRadius: 10, border: 'none', background: 'var(--gd-green)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
    influencerBtn: { padding: '12px 20px', borderRadius: 10, border: '1.5px solid rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: 600, fontSize: 13.5 },
    influencerBtnActive: { background: '#A48FD1', borderColor: '#A48FD1', color: '#1A1529' },

    // Hero's own control row — light-on-dark variants of the regular
    // .gd-input/quickBtn styles, since they sit directly on the dark hero
    // background rather than a white card.
    // nowrap + overflowX so the filter row stays one line, per explicit
    // instruction — a horizontal scroll only kicks in on a very narrow
    // viewport rather than wrapping to a second line.
    heroControlsRow: { position: 'relative', zIndex: 1, display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'center', marginTop: 22, paddingTop: 18, borderTop: '1px solid rgba(255,255,255,0.12)', overflowX: 'auto' },
    heroQuickBtn: { padding: '8px 14px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#C9BFE0', fontWeight: 500, fontSize: 12.5, whiteSpace: 'nowrap' },
    heroQuickBtnActive: { background: '#A48FD1', borderColor: '#A48FD1', color: '#1A1529', fontWeight: 700 },
    heroInput: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.08)', color: '#fff', fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif" },
    heroSelect: { padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.18)', background: '#2A2140', color: '#fff', fontSize: 12.5, fontFamily: "'Space Grotesk', sans-serif" },
    heroShowingText: { position: 'relative', zIndex: 1, fontSize: 12.5, color: '#9A90AE', margin: '12px 0 0' },

    dashboardGrid: { marginBottom: 18 },
    kpiRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 14 },
    kpiTile: { background: 'var(--gd-surface)', border: '1px solid var(--gd-border)', borderRadius: 14, padding: '16px 18px', boxShadow: 'var(--gd-shadow-sm)' },
    kpiLabel: { fontSize: 11, color: 'var(--gd-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: 6, fontWeight: 600 },
    chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 },
    chartCard: { background: 'var(--gd-surface)', border: '1px solid var(--gd-border)', borderRadius: 14, padding: '18px 20px', boxShadow: 'var(--gd-shadow-sm)' },
    chartCardCompact: { background: 'var(--gd-surface-subtle)', border: '1px solid var(--gd-border)', borderRadius: 12, padding: '12px 14px' },
    chartTitle: { fontSize: 13, fontWeight: 700, color: 'var(--gd-text)', marginBottom: 14 },
    chartTitleCompact: { fontSize: 11.5, fontWeight: 700, color: 'var(--gd-text)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.03em' },
    legendRow: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 },
    legendRowCompact: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 },
    legendDot: { width: 10, height: 10, borderRadius: 3, flexShrink: 0 },

    // Summary Report card (compact charts + stock deduction table).
    summaryCard: { background: 'var(--gd-surface)', border: '1px solid var(--gd-border)', borderRadius: 16, padding: '20px 22px', boxShadow: 'var(--gd-shadow-md)', marginBottom: 18 },
    summaryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
    summaryRangeBadge: { fontSize: 11.5, fontWeight: 700, color: 'var(--gd-purple)', background: 'var(--gd-purple-tint)', padding: '4px 12px', borderRadius: 999 },
    summaryGrid: {},
    summaryBottomRow: { display: 'grid', gridTemplateColumns: '0.9fr 1.3fr', gap: 16, alignItems: 'start' },
    compactChartsCol: { display: 'flex', flexDirection: 'column', gap: 10 },
    stockCard: { background: 'var(--gd-surface-subtle)', border: '1px solid var(--gd-border)', borderRadius: 12, padding: '14px 16px' },
    stockTableWrap: { maxHeight: 280, overflowY: 'auto' },
    emptyState: { padding: 48, textAlign: 'center', color: 'var(--gd-text-faint)', fontSize: 14 },
    card: { background: 'var(--gd-surface)', border: '1px solid var(--gd-border)', borderRadius: 16, overflow: 'hidden', boxShadow: 'var(--gd-shadow-md)' },
    scrollWrap: { overflow: 'auto', maxHeight: '65vh' },
    gridRow: { display: 'grid', alignItems: 'center', borderBottom: '1px solid var(--gd-border)' },
    gridHeader: { position: 'sticky', top: 0, background: 'var(--gd-accent-surface)', color: '#fff', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', zIndex: 1 },
    headCell: { padding: '13px 12px', overflow: 'hidden', whiteSpace: 'nowrap' },
    cell: { padding: '12px 12px', fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    numTab: { fontVariantNumeric: 'tabular-nums' },

    modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.5)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
    modal: { background: 'var(--gd-surface)', borderRadius: 18, width: 720, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.35)' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid var(--gd-border)' },
    closeBtn: { background: 'var(--gd-surface-subtle)', border: 'none', borderRadius: 8, width: 30, height: 30, fontSize: 14, cursor: 'pointer', color: 'var(--gd-text-muted)' },
    modalBody: { padding: '18px 22px', overflowY: 'auto', flex: 1 },
    formGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 8 },
    fieldLabel: { fontSize: 11, fontWeight: 700, color: 'var(--gd-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 },
    sectionTitle: { fontSize: 13, fontWeight: 700, color: 'var(--gd-purple)', margin: '18px 0 10px', paddingTop: 12, borderTop: '1px solid var(--gd-border)' },
    lineItemHeaderRow: { display: 'flex', gap: 8, marginBottom: 6, fontSize: 10.5, fontWeight: 700, color: 'var(--gd-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em' },
    lineItemRow: { display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center' },
    skuDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, background: '#fff', border: '1px solid var(--gd-border)', borderRadius: 10, boxShadow: 'var(--gd-shadow-md)', maxHeight: 220, overflowY: 'auto', zIndex: 20 },
    skuOption: { display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 12px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--gd-border)' },
    removeLineBtn: { background: 'var(--gd-error-tint)', color: 'var(--gd-error)', border: 'none', borderRadius: 8, width: 34, height: 34, fontSize: 13, cursor: 'pointer', flexShrink: 0 },
    addLineBtn: { padding: '8px 14px', borderRadius: 8, border: '1.5px dashed var(--gd-purple)', background: 'var(--gd-purple-tint)', color: 'var(--gd-purple)', fontWeight: 700, fontSize: 12.5, marginBottom: 4 },
    errorBanner: { background: 'var(--gd-error-tint)', color: 'var(--gd-error)', borderRadius: 10, padding: '10px 14px', fontSize: 13, marginBottom: 14, fontWeight: 600 },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 22px', borderTop: '1px solid var(--gd-border)' },
    cancelBtn: { padding: '11px 20px', borderRadius: 10, border: '1px solid var(--gd-border-soft)', background: '#fff', color: 'var(--gd-text)', fontWeight: 600, fontSize: 13.5 },
    saveBtn: { padding: '11px 24px', borderRadius: 10, border: 'none', background: 'var(--gd-purple)', color: '#fff', fontWeight: 700, fontSize: 13.5 },
    reviewTable: { width: '100%', borderCollapse: 'collapse', marginBottom: 8, fontSize: 13 },
    reviewLabel: { padding: '8px 10px', color: 'var(--gd-text-faint)', fontWeight: 600, borderBottom: '1px solid var(--gd-border)', width: '38%' },
    reviewValue: { padding: '8px 10px', color: 'var(--gd-text)', borderBottom: '1px solid var(--gd-border)' },
    reviewHeadCell: { padding: '8px 10px', textAlign: 'left', fontSize: 10.5, fontWeight: 700, color: 'var(--gd-text-faint)', textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '2px solid var(--gd-border)' },
};
